/**
 * PhotoCarousel (#88, redesigned 2026-05-03 to match VideoOverlay style)
 *
 * Custom UI wrapping a single image at a time. Same visual language as
 * VideoOverlay so the user recognises both as AmigoMio chrome:
 *   - "TV frame" — black border around the photo
 *   - Branded bottom control bar with the AmigoMio orange accent stripe
 *   - Big finger-friendly buttons in one strip
 *
 * Buttons (left to right): Done | Prev | Next | Counter | Zoom- | Zoom+ | Help
 * The Prev/Next/Counter trio is hidden when there's only one photo.
 *
 * Zoom + pan: zooming above 1× lets the user drag the image with one
 * finger (or mouse) to look around. Touch-events handle mobile/tablet,
 * pointer-events handle desktop. We track translation in state and apply
 * it via CSS transform so React handles the redraws.
 *
 * Help: clicking Help fires an `onHelp` callback that TabletApp uses to
 * tell the server `{type: 'photo_help_requested'}`. The server replies
 * with a TTS explanation — the user hears Berta describe the controls.
 *
 * The auto-advance carousel that the previous version had is gone — if
 * the user is opening multiple photos via SHOW_PHOTO tags they want to
 * look at each one at their own pace. (Live-test feedback 2026-05-03.)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLang } from '../i18n/LanguageContext';
// #441 Phase 2 — i18n bucket for PhotoCarousel strings. Returns null
// when LanguageProvider isn't in the tree (tests).
function _useStrings() {
  const ctx = useLang();
  const s = ctx && ctx.t && ctx.t.tablet && ctx.t.tablet.photocarousel;
  return {
    done: (s && s.done) || 'Done',
    prev: (s && s.prev) || 'Previous photo',
    next: (s && s.next) || 'Next photo',
    zoomIn: (s && s.zoomIn) || 'Zoom in',
    zoomOut: (s && s.zoomOut) || 'Zoom out',
    help: (s && s.help) || 'Help',
    closeAria: (s && s.closeAria) || 'Close photos',
    bertaSaying: (s && s.bertaSaying) || 'Berta is saying',
  };
}

// AmigoMio orange — must match VideoOverlay so the two overlays read
// as parts of the same product.
const BRAND_ORANGE = '#E8845A';

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;

export default function PhotoCarousel({
  photos,
  onHelp,
  onOpened,
  onClosed,
}) {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragState = useRef(null); // { startX, startY, startPanX, startPanY } or null
  const imgWrapRef = useRef(null);

  // Reset zoom/pan when switching photos — otherwise the new photo opens
  // already zoomed and offset, which is disorienting.
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [index]);

  // Tell the server we've opened the carousel so it can be more lenient
  // with silence (the user is looking at a photo, not zoning out).
  useEffect(() => {
    if (onOpened) onOpened();
    return () => { if (onClosed) onClosed(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const photo = photos && photos[index];
  const s = _useStrings();

  const goNext = useCallback(() => {
    if (!photos || photos.length <= 1) return;
    setIndex(i => (i + 1) % photos.length);
  }, [photos]);

  const goPrev = useCallback(() => {
    if (!photos || photos.length <= 1) return;
    setIndex(i => (i - 1 + photos.length) % photos.length);
  }, [photos]);

  const zoomIn = () => setZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => {
    setZoom(z => {
      const next = Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2));
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  };

  // Pointer-based pan — only active when zoomed in. We translate the
  // image element via CSS transform; the parent stays fixed.
  const onPointerDown = (e) => {
    if (zoom <= 1) return;
    // #450 — suppress native drag-start that would otherwise flip the
    // cursor to 🚫 forbidden when the pointer leaves a "drop target".
    // Without this, panning a zoomed photo on some browsers shows the
    // no-drop cursor and feels broken even though our handler does
    // update the transform.
    e.preventDefault();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
    if (imgWrapRef.current && imgWrapRef.current.setPointerCapture) {
      try { imgWrapRef.current.setPointerCapture(e.pointerId); } catch (_) {}
    }
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setPan({
      x: dragState.current.startPanX + dx,
      y: dragState.current.startPanY + dy,
    });
  };
  const onPointerUp = (e) => {
    dragState.current = null;
    if (imgWrapRef.current && imgWrapRef.current.releasePointerCapture) {
      try { imgWrapRef.current.releasePointerCapture(e.pointerId); } catch (_) {}
    }
  };

  if (!photo) return null;

  const showCarouselNav = photos.length > 1;

  return (
    <div style={styles.backdrop}>
      {/* #1089 — pure content: the ActivityFrame owns the state row,
          transcription, and the close (X). Was a dual inline/full-screen
          mode with an embedded CallStrip pill + a Berta caption band. */}

      {/* TV frame around the photo. Same recipe as VideoOverlay. */}
      <div style={styles.tvFrame}>
        <div
          ref={imgWrapRef}
          style={{
            ...styles.imageWrap,
            cursor: zoom > 1 ? (dragState.current ? 'grabbing' : 'grab') : 'default',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          /* #450 — wrapper-level drag suppression: even with
             draggable={false} on the img, some browsers still kick
             off a native drag from a parent container. */
          onDragStart={(e) => e.preventDefault()}
        >
          <img
            src={photo.url}
            alt={photo.caption || ''}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            style={{
              ...styles.image,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transition: dragState.current ? 'none' : 'transform 0.15s ease',
            }}
          />
        </div>
      </div>

      {photo.caption && <div style={styles.caption}>{photo.caption}</div>}

      {/* Single control bar — same visual recipe as VideoOverlay. */}
      <div style={styles.controlBar}>
        {showCarouselNav && (
          <div style={styles.navGroup}>
            <button onClick={goPrev} style={styles.navBtn} aria-label={s.prev}>
              <span style={styles.navIcon}>‹</span>
            </button>
            <div style={styles.counterPill}>
              <span style={styles.counterText}>{index + 1} / {photos.length}</span>
            </div>
            <button onClick={goNext} style={styles.navBtn} aria-label={s.next}>
              <span style={styles.navIcon}>›</span>
            </button>
          </div>
        )}

        <div style={styles.zoomGroup}>
          <button
            onClick={zoomOut}
            disabled={zoom <= ZOOM_MIN}
            style={{ ...styles.zoomBtn, ...(zoom <= ZOOM_MIN ? styles.zoomBtnDisabled : {}) }}
            aria-label={s.zoomOut}
          >
            <span style={styles.zoomBtnIcon}>−</span>
          </button>
          <div style={styles.zoomDisplay}>
            <span style={styles.zoomNumber}>{zoom.toFixed(2).replace(/\.?0+$/, '')}×</span>
          </div>
          <button
            onClick={zoomIn}
            disabled={zoom >= ZOOM_MAX}
            style={{ ...styles.zoomBtn, ...(zoom >= ZOOM_MAX ? styles.zoomBtnDisabled : {}) }}
            aria-label={s.zoomIn}
          >
            <span style={styles.zoomBtnIcon}>+</span>
          </button>
        </div>

        <button
          onClick={() => { if (onHelp) onHelp(); }}
          style={styles.helpBtn}
          aria-label={s.help}
        >
          <span style={styles.helpIcon}>?</span>
          <span style={styles.btnLabel}>{s.help}</span>
        </button>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    // #1089 — fills the ActivityFrame content slot; the frame owns the
    // full-screen positioning + z-index + close. Was fixed/inset/zIndex.
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    background: '#000',
    display: 'flex',
    flexDirection: 'column',
  },
  // 80px pill at the top of the full-screen overlay. Sized to match
  // the phone-portrait CallStrip pill in TabletApp so the strip looks
  // identical whether the photo is full-screen or inline.
  embeddedStrip: {
    flexShrink: 0,
    height: 80,
  },
  // #432 — inline mode: contained inside the parent (focal area of
  // the split call view) instead of full-screen. CallStrip stays
  // visible alongside.
  backdropInline: {
    position: 'relative',
    width: '100%',
    height: '100%',
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // #432 — Berta's reply caption band.
  // #446 — moved from `bottom: 0` to `top: 0` because the bottom band
  // overlapped the photo carousel's control bar (Done / Prev / Next
  // / Counter / Zoom / Help). Real-tablet testing showed the user
  // couldn't tap Done while Berta was speaking. Top placement keeps
  // the controls clear and is less likely to cover faces (people
  // usually fill the centre/lower portion of casual snaps).
  bertaCaptionBand: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 100%)',
    color: 'white',
    padding: '14px 22px',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 4,
    maxHeight: '40%',
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
  },
  bertaCaptionLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.75,
    marginBottom: 4,
  },
  bertaCaptionText: {
    fontSize: 'clamp(16px, 1.7vw, 22px)',
    lineHeight: 1.4,
    fontWeight: 500,
  },
  tvFrame: {
    flex: 1,
    margin: '20px 32px 8px',
    border: '4px solid #1a1a1a',
    borderRadius: '14px',
    background: '#000',
    boxShadow: '0 0 60px rgba(0, 0, 0, 0.7), inset 0 0 0 2px #2c2c2c',
    overflow: 'hidden',
    display: 'flex',
    minHeight: 0,
  },
  imageWrap: {
    flex: 1,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    touchAction: 'none', // disable browser pan/zoom so our handler has the gesture
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    userSelect: 'none',
    pointerEvents: 'none', // wrapper handles pointer events; image must not steal them
    transformOrigin: 'center center',
    willChange: 'transform',
    // #450 — extra belt-and-braces against native HTML drag-and-drop
    // (which would show the 🚫 forbidden cursor when panning a zoomed
    // photo). draggable={false} on the <img> alone isn't enough on
    // some browsers (notably Safari).
    WebkitUserDrag: 'none',
    KhtmlUserDrag: 'none',
    WebkitUserSelect: 'none',
  },
  caption: {
    flexShrink: 0,
    // #452 — was `#ddd` light-grey, OK on the legacy black backdrop but
    // unreadable on the new inline split's cream focal area (#432).
    // Switched to standard ink + a small white text-shadow so the
    // caption stays legible against either backdrop.
    color: '#2C2C2C',
    padding: '8px 32px 6px',
    fontSize: '0.95rem',
    fontWeight: 500,
    textAlign: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    textShadow: '0 1px 0 rgba(255,255,255,0.5)',
  },
  controlBar: {
    flexShrink: 0,
    display: 'flex',
    // #481 — Bas wide-screen test 2026-05-08: on tablet-landscape
    // (focal area ~ callRoot 1280 − strip 360 = ~920 px, minus
    // 48 px focal padding = ~872 px usable), the 8 controls
    // (Done / ‹ / count / › / − / 1× / + / Help) plus their
    // gaps + container padding pushed past the right edge and
    // the rightmost Help button got clipped behind the strip.
    // flexWrap lets the row drop to a second line on narrow
    // viewports rather than overflowing. rowGap keeps the
    // wrapped row visually attached. Tighter horizontal padding
    // + gap give a bit more breathing room before wrapping is
    // needed at all.
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '12px',
    padding: '16px 18px 20px',
    gap: '14px',
    background: 'rgba(28, 28, 30, 0.98)',
    borderTop: `3px solid ${BRAND_ORANGE}`,
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  doneBtn: {
    minWidth: '130px',
    minHeight: '60px',
    padding: '10px 22px',
    border: `2px solid ${BRAND_ORANGE}`,
    borderRadius: '999px',
    background: 'transparent',
    color: '#fff',
    fontSize: '1.1rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  btnIcon: { fontSize: '1.2rem', lineHeight: 1 },
  btnLabel: { letterSpacing: '0.02em' },
  navGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#fff',
  },
  navBtn: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: `2px solid ${BRAND_ORANGE}`,
    background: 'transparent',
    color: '#fff',
    fontSize: '2.2rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: 0,
    lineHeight: 1,
  },
  navIcon: { lineHeight: 1, marginTop: '-4px' },
  counterPill: {
    minWidth: '78px',
    height: '54px',
    padding: '0 14px',
    borderRadius: '999px',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  counterText: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#fff',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  zoomGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#fff',
  },
  zoomBtn: {
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    border: `2px solid ${BRAND_ORANGE}`,
    background: 'transparent',
    color: '#fff',
    fontSize: '1.6rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: 0,
  },
  zoomBtnDisabled: {
    opacity: 0.35,
    cursor: 'not-allowed',
  },
  zoomBtnIcon: { lineHeight: 1 },
  zoomDisplay: {
    minWidth: '70px',
    height: '54px',
    padding: '0 12px',
    borderRadius: '999px',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  zoomNumber: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#fff',
  },
  // Help — primary brand-orange filled, since it's the action that
  // gives the user agency when they don't know what to do.
  helpBtn: {
    minWidth: '120px',
    minHeight: '60px',
    padding: '10px 22px',
    border: 'none',
    borderRadius: '999px',
    background: BRAND_ORANGE,
    color: '#fff',
    fontSize: '1.1rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(232, 132, 90, 0.4)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  helpIcon: {
    fontSize: '1.4rem',
    fontWeight: 700,
    lineHeight: 1,
  },
};
